import React, { JSX, Suspense, useMemo, useState } from "react";

import { Box, Paper, type SelectChangeEvent, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSummarizedHistoryChartQuery } from "src/api/locationChartQueries.api";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import ComparisonTypeDropdown from "src/components/ComparisonTypeDropdown/ComparisonTypeDropdown";
import {
  ChartTooltipFormatterParams,
  type FilteredDashboardComponentParams,
} from "src/components/Dashboard/interfaces/DashboardComponentParams";
import LiveSection from "src/components/LiveSection/LiveSection";
import { chartLiveSectionTooltipFormatter } from "src/components/Tooltip/helpers/chartLiveSectionTooltipFormatter";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { createLabelFormatter, filterByLocationTypeAndSortMeasuresSummarizedDto, sortMeasuresData } from "src/lib/number.helpers";
import ComparisonType from "src/types/ComparisonType";

import PerformersType from "./enum/PerformersType";
import PerformersDropdown from "./LiveSectionDropdown/PerformersDropdown";

const ReactECharts = React.lazy(() => import("echarts-for-react"));

const styles = {
  chartContainer: { height: 250 },
};

function FootfallLastHourComponent({ selectedLocation, displayedType }: FilteredDashboardComponentParams): JSX.Element {
  const [value, setValue] = useState(PerformersType.TopFive);
  const [comparisonValue, setComparisonValue] = useState(ComparisonType.Absolute);

  const handleChange = (event: SelectChangeEvent): void => {
    setValue(Number(event.target.value));
  };

  const handleChangeComparisonType = (event: SelectChangeEvent): void => {
    setComparisonValue(Number(event.target.value));
  };

  const { t } = useTranslation();
  const theme = useTheme();
  const { data: measuresData } = useSummarizedHistoryChartQuery([selectedLocation.Id], IntervalType.Hour, null, MeasureType.Count);

  const filteredMeasuresData = useMemo(
    () => filterByLocationTypeAndSortMeasuresSummarizedDto(measuresData, displayedType),
    [displayedType, measuresData]
  );

  // Sort the data based on selected performers type
  const sortedChartData = useMemo(() => {
    const data = filteredMeasuresData?.data;
    if (!data) return [];

    const isTopOrAll = value === PerformersType.TopFive || value === PerformersType.All;
    const order = isTopOrAll ? "asc" : "desc";
    const sorted = sortMeasuresData([...data], comparisonValue, order);

    if (value === PerformersType.TopFive || value === PerformersType.BottomFive) {
      return sorted.slice(0, 5);
    }

    return sorted;
  }, [filteredMeasuresData, value, comparisonValue]);

  const data = useMemo(() => (sortedChartData ? sortedChartData.map((i) => i.currentValue ?? 0) : []), [sortedChartData]);

  const maxValue = useMemo(() => Math.max(...data, 0), [data]);

  const option = useMemo(
    () => ({
      grid: {
        left: "0%",
        right: "0%",
        bottom: "3%",
        top: "5%",
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params: ChartTooltipFormatterParams) =>
          chartLiveSectionTooltipFormatter(
            params as ChartTooltipFormatterParams,
            sortedChartData,
            dayjs(filteredMeasuresData?.currentDateRange.start),
            dayjs(filteredMeasuresData?.pastDateRange.start),
            theme
          ),
      },
      dataZoom: [
        {
          type: "inside",
          id: "insideY",
          yAxisIndex: 0,
          start: 100,
          end: value === PerformersType.All ? ((data.length - 4) / data.length) * 100 : 50_000,
          zoomOnMouseWheel: false,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
        },
      ],
      xAxis: [
        {
          type: "value",
          max: maxValue,
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          data: sortedChartData.map((i) => i.localName),
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 20,
          barWidth: 4,
          label: {
            color: theme.palette.text.primary,
            position: [0, -20],
            formatter: "{b}",
            show: true,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.typography.fontWeightRegular,
            overflow: "truncate",
            width: 200,
          },
          itemStyle: {
            color: theme.palette.primary.main,
          },
          data,
        },
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 20,
          barWidth: 4,
          label: {
            valueAnimation: true,
            position: "insideTopRight",
            distance: -20,
            formatter: createLabelFormatter(sortedChartData),
            rich: {
              black: {
                fontSize: theme.fontSizes.sm,
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.text.primary,
              },
              differencePositive: {
                color: theme.palette.positive.text,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.typography.fontWeightRegular,
              },
              differenceNegative: {
                color: theme.palette.negative.text,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.typography.fontWeightRegular,
              },
            },
            show: true,
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.typography.fontWeightLight,
          },
          itemStyle: {
            color: theme.palette.map.secondary,
          },
          data: data?.map((i) => (maxValue === 0 ? 1 : maxValue - i + maxValue)),
        },
      ],
    }),
    [sortedChartData, filteredMeasuresData?.currentDateRange.start, filteredMeasuresData?.pastDateRange.start, data, maxValue, theme, value]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <Box sx={sharedStyles(theme).spaceBetweenFlexbox}>
        <Typography
          color={theme.palette.text.secondary}
          fontSize={theme.fontSizes.sm}
          fontWeight={theme.typography.fontWeightRegular}
          textTransform='uppercase'
        >
          {t("Footfall last hour")}
        </Typography>
        <Box display='flex' mt={-1}>
          <LiveSection />
        </Box>
      </Box>
      <Box mt={2} sx={sharedStyles(theme).spaceBetweenFlexbox}>
        <PerformersDropdown value={value} onChange={handleChange} />
        <ComparisonTypeDropdown value={comparisonValue} onChange={handleChangeComparisonType} />
      </Box>
      <Box mt={2}>
        <Box sx={styles.chartContainer}>
          <Suspense fallback={<ChartSkeleton />}>{filteredMeasuresData ? <ReactECharts option={option} /> : <ChartSkeleton />}</Suspense>
        </Box>
      </Box>
    </Paper>
  );
}
export default FootfallLastHourComponent;
